import { useState } from 'react'

import { thousands } from '@/lib/format'

export interface BarItem {
  key: string
  label: string
  value: number
  detail?: string
}

/**
 * Horizontal bars for nominal categories. One series, one colour - a
 * darker-where-bigger ramp would double-encode the bar length.
 */
export function BarList({
  items,
  emptyLabel = 'Nothing to show yet.',
  valueFormat = thousands,
}: {
  items: BarItem[]
  emptyLabel?: string
  valueFormat?: (value: number) => string
}) {
  const [hover, setHover] = useState<string | null>(null)
  const max = Math.max(...items.map((item) => item.value), 1)

  if (!items.length) {
    return <p className="px-4 py-8 text-center text-sm text-ink-muted">{emptyLabel}</p>
  }

  return (
    <ul className="space-y-2 px-4 py-4">
      {items.map((item) => (
        <li
          key={item.key}
          className="group"
          onMouseEnter={() => setHover(item.key)}
          onMouseLeave={() => setHover(null)}
        >
          <div className="flex items-baseline justify-between gap-3">
            <span className="truncate text-xs text-ink" title={item.label}>
              {item.label}
            </span>
            <span className="shrink-0 text-xs font-medium tabular text-ink">
              {valueFormat(item.value)}
            </span>
          </div>
          <div className="mt-1 h-2 w-full overflow-hidden rounded-full" style={{ background: 'var(--surface-2)' }}>
            <div
              className="h-full rounded-full transition-[width] duration-300"
              style={{
                width: `${Math.max((item.value / max) * 100, item.value > 0 ? 2 : 0)}%`,
                background: 'var(--series-1)',
                opacity: hover && hover !== item.key ? 0.45 : 1,
              }}
            />
          </div>
          {item.detail && <p className="mt-0.5 text-xs text-ink-faint">{item.detail}</p>}
        </li>
      ))}
    </ul>
  )
}
